import { useEffect, useState } from 'react';
import Navigation from '@/components/navigation';
import Footer from '@/components/footer';
import SEO from '@/components/SEO';
import { Link } from 'wouter';
import { Calendar, User, ArrowRight } from 'lucide-react';

interface BlogPost {
  slug: string;
  title: string;
  description: string;
  date: string;
  author: string;
  keywords: string;
  excerpt: string;
}

export default function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/blog')
      .then(res => res.json())
      .then(data => {
        setPosts(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading blog posts:', error);
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50">
      <SEO
        title="Waterfront Real Estate Blog - Miami, Fort Lauderdale & Boca Raton Insights"
        description="Expert insights on South Florida waterfront real estate. Market trends, luxury home guides, investment strategies, and local community spotlights for Miami, Fort Lauderdale, and Boca Raton."
        keywords="waterfront real estate blog, Miami real estate market, Fort Lauderdale luxury homes, Boca Raton property guide, South Florida investment tips"
      />
      <Navigation />
      
      <div className="container mx-auto px-4 py-16 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Waterfront Living Insights
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Expert guidance on South Florida's luxury waterfront real estate market. Discover market trends, 
            investment strategies, and insider tips for Miami, Fort Lauderdale, and Boca Raton properties.
          </p>
        </div>

        {/* Blog Posts Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading articles...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-600 text-lg">No blog posts available yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <article
                key={post.slug}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300 flex flex-col"
              >
                {/* Card Content */}
                <div className="p-6 flex-grow flex flex-col">
                  {/* Meta Info */}
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      <span>{post.author}</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl font-bold text-gray-900 mb-3 hover:text-blue-600 transition-colors">
                    <Link href={`/blog/${post.slug}`}>
                      <a className="hover:underline">{post.title}</a>
                    </Link>
                  </h2>

                  {/* Description */}
                  <p className="text-gray-600 mb-4 flex-grow line-clamp-3">
                    {post.description}
                  </p>

                  {/* Read More Link */}
                  <Link href={`/blog/${post.slug}`}>
                    <a className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold group">
                      Read Full Article
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-20 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-12 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Find Your Dream Waterfront Property?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Browse our exclusive collection of luxury oceanfront and bayfront homes
          </p>
          <Link href="/buy">
            <a className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors">
              View Properties
            </a>
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}

